/**
 * Utility helpers used across attribution scripts.
*/

var AttributionUtils = (function () {
  'use strict';

  var ATTR_DEBUG = false;

  var USER_KEY = 'lmj_user_id';
  var SESSION_KEY = 'lmj_session_id';
  var SESSION_TS_KEY = 'lmj_session_last_activity';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  function log() {
    if (ATTR_DEBUG) console.log.apply(console, arguments);
  }

  function generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Persistent user ID (long-lived)
   */
  function getOrCreateUserId() {
    try {
      var existing = localStorage.getItem(USER_KEY);
      if (existing) return existing;

      var id = generateUuid();
      localStorage.setItem(USER_KEY, id);
      log('[Attribution][Utils] New lmj_user_id:', id);
      return id;
    } catch (e) {
    console.error("something went wrong with getOrCreateUserId()", e);
      return generateUuid();
    }
  }

  /**
   * Updates session last-activity timestamp
  */
  function touchSessionActivity() {
    log('[Attribution][Utils] Touched session activity:');
    var now = Date.now();
    localStorage.setItem(SESSION_TS_KEY, now);

    var stored = AttributionStorage.read();
    if (stored) {
      stored.session_last_activity_at = new Date(now).toISOString();
      stored.updated_at = new Date().toISOString();
      AttributionStorage.storePayload(stored);
    }
  }

  /**
   * Session ID with inactivity timeout
   */
  function getOrCreateSessionId() {
  try {
    var now = Date.now();
    var lastActivity = parseInt(localStorage.getItem(SESSION_TS_KEY), 10);
    var existing = localStorage.getItem(SESSION_KEY);
    var stored = AttributionStorage.read() || {};

    // Active session
    if (existing && lastActivity && now - lastActivity < SESSION_TIMEOUT_MS) {
      touchSessionActivity();

      return {
        id: existing,
        sessionEntryUrl: stored.session_entry_url || null,
        sessionEntryReferrer: stored.session_entry_referrer_external || null
      };
    }

    // New session
    var id = generateUuid();
    localStorage.setItem(SESSION_KEY, id);
    touchSessionActivity();

    var sessionEntryUrl = window.__LMJ_INITIAL_HREF__ || window.location.href;
    var sessionEntryReferrer = null;

    var ref = document.referrer || null;
    if (ref) {
      try {
        var refUrl = new URL(ref);
        if (refUrl.hostname !== window.location.hostname) {
          sessionEntryReferrer = ref;
        }
      } catch (e) {}
    }

    // Persist ONCE
    stored.session_entry_url = sessionEntryUrl;
    stored.session_entry_referrer_external = sessionEntryReferrer;
    stored.lmj_session_id = id;
    stored.updated_at = new Date().toISOString();

    AttributionStorage.storePayload(stored);

    return {
      id: id,
      sessionEntryUrl: sessionEntryUrl,
      sessionEntryReferrer: sessionEntryReferrer
    };
  } catch (e) {
    console.error("something went wrong with getOrCreateSessionId()", e);
    return {
      id: generateUuid(),
      sessionEntryUrl: null,
      sessionEntryReferrer: null
    };
  }
}

  /**
   * Returns the browser-provided referrer.
   * May be empty due to privacy settings, HTTPS → HTTP, or direct visits.
   */
  function getReferrer() {
    var ref = document.referrer || null;
    log('[Attribution][Utils] Document referrer:', ref);
    return ref;
  }

   /**
   * Extracts supported tracking parameters from the URL.
   * Ignores unrelated query params intentionally.
   */
  function getUrlParameters() {
    log('[Attribution][Utils] Reading URL parameters');

    var params = {};
    var search = window.location.search.substring(1);
    if (!search) {
      log('[Attribution][Utils] No query string found');
      return params;
    }

    search.split('&').forEach(function (pair) {
      var parts = pair.split('=');
      if (parts.length !== 2) return;

      var key = decodeURIComponent(parts[0]);
      var value = decodeURIComponent(parts[1]);

      // Only allow known attribution identifiers
      if (
        key.indexOf('utm_') === 0 ||
        key === 'gclid' ||
        key === 'gbraid' ||
        key === 'wbraid'
      ) {
        params[key] = value;
      }
    });

    log('[Attribution][Utils] Captured params:', params);
    return params;
  }

  /**
   * Returns ALL URL query parameters with no filtering.
   * Used for forensic / raw evidence only.
   */
  function getAllUrlParameters() {
    var params = {};
    var search = window.location.search.substring(1);
    if (!search) return params;

    search.split('&').forEach(function (pair) {
      var parts = pair.split('=');
      if (parts.length !== 2) return;

      var key = decodeURIComponent(parts[0]);
      var value = decodeURIComponent(parts[1]);
      params[key] = value;
    });

    return params;
  }

  /**
   * Detects Google Merchant Center / Free Listing feed parameters.
   * These must NEVER be persisted as attribution.
   */
  function isFeedAttribution(params) {
    if (!params) return false;

    var isFeed =
      params.utm_medium === 'product_sync' ||
      params.utm_campaign === 'sag_organic' ||
      params.utm_content === 'sag_organic';

    if (isFeed) {
      log('[Attribution][Utils] Feed attribution detected:', params);
    }

    return isFeed;
  }

  /**
   * Removes tracking parameters from the URL after capture
   * without triggering a page reload.
   */
  function cleanTrackingParameters() {
    log('[Attribution][Utils] Attempting URL cleanup');

    if (!window.history || !window.history.replaceState) {
      log('[Attribution][Utils] History API not available');
      return;
    }

    var url = new URL(window.location.href);
    var keys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'gclid',
      'gbraid',
      'wbraid'
    ];

    var modified = false;

    keys.forEach(function (key) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        modified = true;
      }
    });

    if (modified) {
      window.history.replaceState({}, '', url.toString());
      log('[Attribution][Utils] URL cleaned');
    } else {
      log('[Attribution][Utils] No tracking params to clean');
    }
  }

  /**
   * Derives a basic, non-invasive device context
   * from the user agent string.
   */
  function getDeviceContext() {
    var ua = navigator.userAgent || '';
    var deviceType = /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop';

    var browser = 'unknown';
    if (/Chrome/i.test(ua)) browser = 'Chrome';
    else if (/Safari/i.test(ua)) browser = 'Safari';
    else if (/Firefox/i.test(ua)) browser = 'Firefox';
    else if (/Edg/i.test(ua)) browser = 'Edge';

    var os = 'unknown';
    if (/Windows/i.test(ua)) os = 'Windows';
    else if (/Mac OS/i.test(ua)) os = 'macOS';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/iPhone|iPad/i.test(ua)) os = 'iOS';

    var context = {
      device_type: deviceType,
      browser_family: browser,
      os_family: os
    };

    log('[Attribution][Utils] Device context:', context);
    return context;
  }

  /**
   * Check if two attribution objects are equal
   */
  function areAttributionsEqual(a, b) {
    if (!a && !b) return true;
    if (!a || !b) return false;
    
    var aKeys = Object.keys(a).sort();
    var bKeys = Object.keys(b).sort();
    
    if (aKeys.length !== bKeys.length) return false;
    
    for (var i = 0; i < aKeys.length; i++) {
      var key = aKeys[i];
      if (a[key] !== b[key]) return false;
    }
    
    return true;
  }

  return {
    getOrCreateUserId: getOrCreateUserId,
    getUrlParameters: getUrlParameters,
    getAllUrlParameters: getAllUrlParameters,
    isFeedAttribution: isFeedAttribution,
    cleanTrackingParameters: cleanTrackingParameters,
    getOrCreateSessionId: getOrCreateSessionId,
    getDeviceContext: getDeviceContext,
    getReferrer: getReferrer,
    areAttributionsEqual: areAttributionsEqual,
    touchSessionActivity: touchSessionActivity,
  };

})();